// Level2Logic (cmmc20) — Feedback Widget Component
// Shows star rating prompt after user engagement

import { useState, useEffect } from 'react';
import { shouldShowFeedback, submitFeedback } from '../services/feedbackService';

export function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [feature, setFeature] = useState('dashboard');
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldShowFeedback()) {
        setShowPrompt(true);
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = () => {
    submitFeedback({ rating, comment, feature, timestamp: new Date() });
    setIsOpen(false);
    setComment('');
    setRating(0);
  };

  if (!showPrompt && !isOpen) return null;

  return (
    <>
      {showPrompt && !isOpen && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="bg-white rounded-lg shadow-xl border p-4 max-w-xs">
            <p className="text-sm font-medium mb-2">How's your experience so far?</p>
            <div className="flex gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => { setRating(star); setIsOpen(true); setShowPrompt(false); }}
                  className="text-2xl text-yellow-400 hover:scale-110 transition-transform"
                  aria-label={`${star} star${star !== 1 ? 's' : ''}`}
                >
                  ★
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowPrompt(false)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="bg-white rounded-lg shadow-xl border p-4 w-80">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sm">Share feedback</h4>
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground text-xs"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Feature</label>
                <select
                  value={feature}
                  onChange={(e) => setFeature(e.target.value)}
                  className="w-full border border-input rounded-md text-sm px-2 py-1.5"
                >
                  <option value="dashboard">Dashboard</option>
                  <option value="threat-intel">Threat Intelligence</option>
                  <option value="abuse-alerts">Abuse Alerts</option>
                  <option value="audit-export">Audit Export</option>
                  <option value="demo-mode">Demo Mode</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground block mb-1">Rating</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className={`text-2xl transition-transform hover:scale-110 ${
                        star <= rating ? 'text-yellow-400' : 'text-gray-300'
                      }`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground block mb-1">Comments (optional)</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="What could we improve?"
                  className="w-full border border-input rounded-md text-sm px-2 py-1.5 h-16 resize-none"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={rating === 0}
                className="w-full bg-primary text-white text-sm py-2 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
